import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Banknote, QrCode, X } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton } from '@mlm/ui';
import {
  cancelPayoutRequest,
  createPayoutRequest,
  getCashoutWindow,
  getMe,
  getSlotWallet,
  listMyPayouts,
  listPayoutMethods,
  listPayoutQrs,
  previewPayout,
  type PayoutMethodField,
  type PayoutPreview,
  type PayoutRequest,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { resolveCashoutFieldLock } from '../lib/cashout-field-lock';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { fmtCurrency, fmtDateTime } from '../lib/format';
import { WalletPanel } from '../components/wallet/wallet-panel';
import { SegmentedToggle } from '../components/segmented-toggle';

const AMOUNT_RE = /^\d+(\.\d{1,4})?$/;

// Friendly copy for the few request errors a member can actually hit; anything
// else falls back to the humanized error code.
const PAYOUT_ERROR_MESSAGES: Record<string, string> = {
  slot_in_debt:
    'This slot still has an open CD debt. Its cash earnings pay the debt off first — you can cash out once it settles.',
  cashout_account_taken:
    'This account is already registered to another member. Use a cash-out account that belongs to you.',
  payout_qr_required:
    'A payout QR code is required for this method. Save one under Settings → Payout QR, then try again.',
  missing_account_field: 'Please fill in all the account details for this method.',
  invalid_account_field: 'One of the account details has a value that is not among the choices.',
};

export function CashOutPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { activeSlotLabel, activeSlotId } = useMySlots();

  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: !!activeSlotId,
  });

  // History filters. Dates go to the server as LOCAL-day boundaries — a bare
  // `new Date('YYYY-MM-DD')` is UTC midnight, which shifts the window on any
  // non-UTC tenant. The query key extends the `['payouts', activeSlotId]`
  // prefix, so the submit/cancel invalidations below still hit every page.
  const [histMethod, setHistMethod] = useState('');
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo] = useState('');
  const [histDir, setHistDir] = useState<'desc' | 'asc'>('desc');
  const [histPage, setHistPage] = useState(1);
  const HIST_PAGE_SIZE = 20;

  const payoutsQ = useQuery({
    queryKey: ['payouts', activeSlotId, histMethod, histFrom, histTo, histDir, histPage],
    queryFn: () =>
      listMyPayouts({
        slotId: activeSlotId ?? undefined,
        method: histMethod || undefined,
        from: histFrom ? new Date(`${histFrom}T00:00:00`).toISOString() : undefined,
        to: histTo ? new Date(`${histTo}T23:59:59.999`).toISOString() : undefined,
        dir: histDir,
        page: histPage,
        pageSize: HIST_PAGE_SIZE,
      }),
    enabled: !!activeSlotId,
  });

  // Payout methods are admin-managed — fetched, not hardcoded.
  const methodsQ = useQuery({ queryKey: ['payout-methods'], queryFn: listPayoutMethods });
  // `?? []` mints a new array every render, which would re-run the
  // default-selection effect below on each one.
  const methods = useMemo(() => methodsQ.data ?? [], [methodsQ.data]);

  // A member blocked from cashing out an in-debt CD slot can pay it off by
  // topping up (when both the top-up feature and the settle-by-top-up policy are on).
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const topupPayoffHref =
    meQ.data?.tenantPolicy?.walletTopupEnabled && meQ.data?.tenantPolicy?.cdTopupSettlesDebt
      ? '/wallet-topup'
      : null;

  // Cash-out window (admin schedule). The server also enforces it on submit.
  const windowQ = useQuery({ queryKey: ['cashout-window'], queryFn: getCashoutWindow });
  const cashoutOpen = windowQ.data?.open ?? true;

  // Min/max amount bounds (server-enforced; mirrored here for a hint + early
  // disable). Effective single-request min respects the per-member min too.
  const limits = windowQ.data?.limits;
  const effMin = Math.max(limits?.perSlotMin ?? 0, limits?.perMemberMin ?? 0);
  const effMax = limits?.perSlotMax ?? null;

  // KYC gate mirrors the tenant flag: only block when the tenant requires KYC
  // and the member isn't verified. Default to requiring it until the window
  // query resolves (matches the server default).
  const requireKyc = windowQ.data?.requireKyc ?? true;
  const kycOk = !requireKyc || user?.kycStatus === 'verified';

  // Multiple-cashout gate (server-enforced; mirrored for an early disable +
  // hint). `payoutsQ` is scoped to the active slot, which exactly covers the
  // 'slot' scope and the common case of 'member' scope; for a pending request
  // on a DIFFERENT slot under 'member' scope the server still rejects.
  // Payout-QR gate mirrors the tenant flag. The same query key as
  // `PayoutQrNotice` below, so TanStack dedupes it to one request; we need the
  // answer up here to disable submit. Default to NOT required until the window
  // query resolves (matches the server default).
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });
  const qrRequired = windowQ.data?.payoutQrRequired === true;

  const multipleAllowed = windowQ.data?.multipleCashoutRequestsAllowed ?? true;
  const cancellationAllowed = windowQ.data?.cancellationAllowed ?? true;
  const hasPendingRequest = (payoutsQ.data ?? []).some((p) =>
    ['requested', 'queued', 'processing'].includes(p.status),
  );
  const blockedByPending = !multipleAllowed && hasPendingRequest;

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  // Account inputs, keyed by field key (or `account` for the single-field default).
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // MUST stay below `method`'s useState: `.some()` runs its callback
  // synchronously, so reading `method` above the declaration throws a TDZ
  // ReferenceError the moment `qrsQ` resolves with a non-empty list — which
  // blanked the page for every member who had a payout QR saved.
  const hasQrForMethod = (qrsQ.data ?? []).some((q) => q.methodCode === method);

  // Default the selection to the first method once they load (or repair it if
  // the current pick is no longer offered).
  useEffect(() => {
    if (methods.length === 0) return;
    if (!methods.some((m) => m.code === method)) setMethod(methods[0]!.code);
  }, [methods, method]);

  const selectedMethod = methods.find((m) => m.code === method);

  // The tenant-global flag forces every method; a per-method `requiresQr`
  // narrows it to one (mirrors `payoutQrRequiredFor` on the server). MUST stay
  // below `selectedMethod` for the same TDZ reason as `hasQrForMethod` above.
  const requiresQrForMethod = qrRequired || selectedMethod?.requiresQr === true;
  const qrOk = !requiresQrForMethod || hasQrForMethod;
  const noMethods = methodsQ.isSuccess && methods.length === 0;

  // The inputs to render: admin-defined `fields`, or the single legacy account
  // field (stored under `account`) when the method defines none.
  const accountFields: PayoutMethodField[] =
    selectedMethod && selectedMethod.fields.length > 0
      ? selectedMethod.fields
      : [
          {
            key: 'account',
            label: selectedMethod?.accountLabel ?? 'Account details',
            placeholder: selectedMethod?.accountPlaceholder ?? '',
            required: true,
          },
        ];

  // Cash-out security: when the tenant locks the holder name to the owner, the
  // account-holder field is forced to the member's own full name (the server
  // re-applies this authoritatively — this just makes the form reflect it).
  const lockName = windowQ.data?.lockCashoutNameToOwner === true;
  const ownerFullName = meQ.data?.fullName ?? '';

  // Contact autofill: fields tagged email/contact_number are seeded from the
  // member's own profile. 'prefill' keeps them editable; 'locked' also renders
  // them read-only (the server re-applies locked values authoritatively).
  const contactMode = windowQ.data?.cashoutContactAutofill ?? 'off';
  const ownerEmail = meQ.data?.email ?? '';
  const ownerPhone = meQ.data?.phone ?? '';

  // Reset entered values when the method changes (keys differ per method).
  useEffect(() => {
    setFieldValues({});
  }, [method]);

  // Seed the locked holder-name field with the owner's full name.
  useEffect(() => {
    if (!lockName || !ownerFullName) return;
    const holder = accountFields.find((f) => f.role === 'account_holder');
    if (holder) setFieldValues((v) => ({ ...v, [holder.key]: ownerFullName }));
    // Re-seed when the method changes (fields differ) or the name/flag resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockName, ownerFullName, method]);

  // Seed email/contact-number fields from the profile (prefill AND locked —
  // seeding `fieldValues`, not just displaying, is what makes `accountComplete`
  // and the submitted payload correct). Empty profile values are never seeded:
  // the field stays editable + required so a member with no phone on file can
  // still type one (mirrors the server's empty-profile fallback).
  useEffect(() => {
    if (contactMode === 'off') return;
    const seeds: Record<string, string> = {};
    for (const f of accountFields) {
      // Widened: lib/api.ts's local role union predates email/contact_number.
      const role: string | undefined = f.role;
      if (role === 'email' && ownerEmail) seeds[f.key] = ownerEmail;
      if (role === 'contact_number' && ownerPhone) seeds[f.key] = ownerPhone;
    }
    if (Object.keys(seeds).length > 0) setFieldValues((v) => ({ ...v, ...seeds }));
    // Re-seed when the method changes (fields differ) or the profile/mode resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactMode, ownerEmail, ownerPhone, method]);

  const accountComplete = accountFields.every(
    (f) => f.required === false || (fieldValues[f.key]?.trim() ?? '') !== '',
  );
  const payload: Record<string, string> = {};
  for (const f of accountFields) {
    const v = fieldValues[f.key]?.trim();
    if (v) payload[f.key] = v;
  }

  // Debounce the amount so the live breakdown preview doesn't fire on every
  // keystroke. The math is server-authoritative (per-method brackets), so we
  // ask the API rather than recomputing in the client.
  const [debouncedAmount, setDebouncedAmount] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAmount(amount), 350);
    return () => clearTimeout(t);
  }, [amount]);

  const amountValid = AMOUNT_RE.test(debouncedAmount) && Number(debouncedAmount) > 0;
  const amountNum = Number(amount);
  const amountOutOfBounds =
    !!amount && ((effMin > 0 && amountNum < effMin) || (effMax != null && amountNum > effMax));
  const previewQ = useQuery({
    queryKey: ['payout-preview', activeSlotId, debouncedAmount, method],
    queryFn: () => previewPayout({ slotId: activeSlotId!, amount: debouncedAmount, method }),
    enabled: !!activeSlotId && amountValid && !!method,
  });
  const preview = previewQ.data;
  const netNonPositive = !!preview && !preview.netPositive;

  const createMut = useMutation({
    meta: { successMessage: 'Payout requested.' },
    mutationFn: () =>
      createPayoutRequest({
        slotId: activeSlotId!,
        amount,
        method,
        payload,
      }),
    onSuccess: () => {
      setAmount('');
      setFieldValues({});
      setError(null);
      qc.invalidateQueries({ queryKey: ['payouts', activeSlotId] });
      qc.invalidateQueries({ queryKey: ['wallet', activeSlotId] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error ?? 'request_failed');
    },
  });

  const cancelMut = useMutation({
    meta: { successMessage: 'Payout request cancelled.' },
    mutationFn: cancelPayoutRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts', activeSlotId] });
      qc.invalidateQueries({ queryKey: ['wallet', activeSlotId] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Banknote className="h-6 w-6 text-muted-foreground" /> Cash Out
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Request a payout from this slot's wallet:
          <span className="ml-1 font-medium text-foreground">{activeSlotLabel}</span>
        </p>
      </div>

      {walletQ.data ? (
        <WalletPanel
          wallet={walletQ.data}
          slotLabel={activeSlotLabel}
          hideCashOut
          topupPayoffHref={topupPayoffHref}
        />
      ) : (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!kycOk && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              Your KYC isn't verified yet. Complete KYC in Settings to request payouts.
            </div>
          )}
          {noMethods && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              No payout methods are available yet. Please contact your administrator.
            </div>
          )}
          {!cashoutOpen && windowQ.data && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              Cash-out is currently closed. It's open {windowQ.data.schedule.openHour}:00–
              {windowQ.data.schedule.closeHour}:00 ({windowQ.data.timezone}) on selected days.
            </div>
          )}
          {blockedByPending && (
            <div className="text-xs rounded-md border border-warning/30 bg-warning/10 p-3">
              You already have a pending cash-out request. Wait until it's paid or cancelled before
              requesting another.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Amount</label>
              <Input
                type="number"
                value={amount}
                step="0.01"
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              {(effMin > 0 || effMax != null) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {effMin > 0 && `Min ${fmtCurrency(effMin)}`}
                  {effMin > 0 && effMax != null && ' · '}
                  {effMax != null && `Max ${fmtCurrency(effMax)} per request`}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                disabled={noMethods}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {methods.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            {accountFields.map((f) => {
              // Non-null when this field is server-locked to a profile value
              // (name lock, or locked contact autofill). A prefill-seeded field
              // resolves null and renders as a normal editable input.
              const lock = resolveCashoutFieldLock(f, {
                lockName,
                contactMode,
                ownerFullName,
                ownerEmail,
                ownerPhone,
              });
              return (
                <div key={f.key}>
                  <label className="text-xs text-muted-foreground">
                    {f.label}
                    {f.required === false && (
                      <span className="ml-1 text-muted-foreground/70">(optional)</span>
                    )}
                  </label>
                  {/* A profile lock wins over everything; then an admin-enumerated
                      field renders as a select (e.g. Savings / Checking) — the
                      server rejects values outside the list, so don't offer a
                      text box it would bounce. */}
                  {!lock && f.options && f.options.length > 0 ? (
                    <select
                      value={fieldValues[f.key] ?? ''}
                      onChange={(e) => setFieldValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select…</option>
                      {f.options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={lock ? lock.value : (fieldValues[f.key] ?? '')}
                      onChange={(e) => setFieldValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      placeholder={f.placeholder || ''}
                      readOnly={!!lock}
                      className={lock ? 'bg-muted' : undefined}
                    />
                  )}
                  {lock && <p className="mt-1 text-[11px] text-muted-foreground">{lock.caption}</p>}
                </div>
              );
            })}
          </div>
          <PayoutQrNotice
            methodCode={method}
            methodLabel={selectedMethod?.label ?? method}
            required={requiresQrForMethod}
          />
          {amountValid && preview && <PayoutBreakdown preview={preview} />}
          {netNonPositive && (
            <p className="text-xs text-destructive">
              Deductions exceed this amount — nothing would be left to pay out. Request a larger
              amount.
            </p>
          )}
          {amountOutOfBounds && (
            <p className="text-xs text-destructive">
              Amount must be{effMin > 0 ? ` at least ${fmtCurrency(effMin)}` : ''}
              {effMin > 0 && effMax != null ? ' and' : ''}
              {effMax != null ? ` at most ${fmtCurrency(effMax)}` : ''}.
            </p>
          )}
          {error && (
            <p className="text-xs text-destructive">
              {PAYOUT_ERROR_MESSAGES[error] ?? error.replace(/_/g, ' ')}
            </p>
          )}
          <Button
            onClick={() => createMut.mutate()}
            disabled={
              !kycOk ||
              !qrOk ||
              !amount ||
              !accountComplete ||
              !method ||
              createMut.isPending ||
              !activeSlotId ||
              netNonPositive ||
              amountOutOfBounds ||
              !cashoutOpen ||
              blockedByPending
            }
            className="w-full"
          >
            {createMut.isPending
              ? 'Submitting…'
              : !cashoutOpen
                ? 'Cash-out closed'
                : blockedByPending
                  ? 'Request pending'
                  : !qrOk
                    ? 'QR code required'
                    : 'Request payout'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-end gap-2 border-b p-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={histMethod}
                onChange={(e) => {
                  setHistMethod(e.target.value);
                  setHistPage(1);
                }}
              >
                <option value="">All</option>
                {methods.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                value={histFrom}
                onChange={(e) => {
                  setHistFrom(e.target.value);
                  setHistPage(1);
                }}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                value={histTo}
                onChange={(e) => {
                  setHistTo(e.target.value);
                  setHistPage(1);
                }}
              />
            </div>
            <SegmentedToggle
              ariaLabel="Sort order"
              options={[
                { value: 'desc', label: 'Newest' },
                { value: 'asc', label: 'Oldest' },
              ]}
              value={histDir}
              onChange={(v) => {
                setHistDir(v);
                setHistPage(1);
              }}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setHistMethod('');
                setHistFrom('');
                setHistTo('');
                setHistDir('desc');
                setHistPage(1);
              }}
              disabled={!histMethod && !histFrom && !histTo && histDir === 'desc'}
            >
              Clear
            </Button>
          </div>
          {payoutsQ.isLoading ? (
            <div className="p-6">
              <Skeleton className="h-20" />
            </div>
          ) : !payoutsQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">
              {histPage > 1 ? 'No more payout requests.' : 'No payout requests match.'}
            </p>
          ) : (
            <ul className="divide-y">
              {payoutsQ.data.map((p) => (
                <PayoutRow
                  key={p.id}
                  p={p}
                  onCancel={() => cancelMut.mutate(p.id)}
                  canceling={cancelMut.isPending}
                  cancellationAllowed={cancellationAllowed}
                />
              ))}
            </ul>
          )}
          {/* The server pages at HIST_PAGE_SIZE; a full page means there may be
              more (no COUNT round-trip for a bounded personal history). */}
          {(histPage > 1 || (payoutsQ.data?.length ?? 0) === HIST_PAGE_SIZE) && (
            <div className="flex items-center justify-between border-t p-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setHistPage((p) => Math.max(1, p - 1))}
                disabled={histPage === 1 || payoutsQ.isFetching}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {histPage}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setHistPage((p) => p + 1)}
                disabled={(payoutsQ.data?.length ?? 0) < HIST_PAGE_SIZE || payoutsQ.isFetching}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PayoutRow({
  p,
  onCancel,
  canceling,
  cancellationAllowed,
}: {
  p: PayoutRequest;
  onCancel: () => void;
  canceling: boolean;
  cancellationAllowed: boolean;
}) {
  // Read the snapshot's applied order so history matches the breakdown card the
  // member saw when requesting (these two used to disagree: the card rendered
  // service → admin → tax while this line rendered tax → service → admin).
  const snapshotSteps = p.deductions?.steps;
  const parts: string[] =
    Array.isArray(snapshotSteps) && snapshotSteps.length > 0
      ? snapshotSteps.map((s) => `${s.label} −${fmtCurrency(s.amount, p.currency)}`)
      : [];
  if (parts.length === 0) {
    // Requests issued before `steps` was snapshotted.
    if (Number(p.serviceCharge) > 0)
      parts.push(`Service −${fmtCurrency(p.serviceCharge, p.currency)}`);
    if (Number(p.adminFee) > 0) parts.push(`Admin −${fmtCurrency(p.adminFee, p.currency)}`);
    if (Number(p.withholdingTax) > 0)
      parts.push(`WTAX −${fmtCurrency(p.withholdingTax, p.currency)}`);
  }
  const hasDeductions = parts.length > 0;
  return (
    <li className="flex items-center gap-3 px-6 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-mono tabular-nums">
          {fmtCurrency(p.netAmount, p.currency)}
          {hasDeductions && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">net</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {hasDeductions ? `Gross ${fmtCurrency(p.amount, p.currency)} · ` : ''}
          {p.method.replace('_', ' ')} · {fmtDateTime(p.requestedAt)}
        </div>
        {hasDeductions && (
          <div className="text-xs text-muted-foreground mt-0.5">{parts.join(' · ')}</div>
        )}
        {p.rejectionReason && (
          <div className="text-xs text-destructive mt-1">Rejected: {p.rejectionReason}</div>
        )}
      </div>
      <Badge variant={badgeFor(p.status)}>{p.status}</Badge>
      {p.status === 'requested' && cancellationAllowed && (
        <Button size="sm" variant="outline" onClick={onCancel} disabled={canceling}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      )}
    </li>
  );
}

/** Live net-payout breakdown shown under the request form. */
function PayoutBreakdown({ preview }: { preview: PayoutPreview }) {
  const c = preview.currency;
  // `steps` is the server's applied sequence — render it verbatim so the member
  // sees the deductions in the order they were actually taken. The fallback
  // covers a preview served by an older API build.
  const rows: Array<{ label: string; value: string }> =
    preview.steps && preview.steps.length > 0
      ? preview.steps.map((s) => ({
          label: s.mode === 'percent' ? `${s.label} (${String(Number(s.value))}%)` : s.label,
          value: `−${fmtCurrency(s.amount, c)}`,
        }))
      : [];
  if (rows.length === 0) {
    if (Number(preview.serviceCharge) > 0)
      rows.push({ label: 'Service charge', value: `−${fmtCurrency(preview.serviceCharge, c)}` });
    if (Number(preview.adminFee) > 0)
      rows.push({ label: 'Admin fee', value: `−${fmtCurrency(preview.adminFee, c)}` });
    if (Number(preview.withholdingTax) > 0)
      rows.push({
        label: `Withholding tax (${String(Number(preview.withholdingTaxRate))}%)`,
        value: `−${fmtCurrency(preview.withholdingTax, c)}`,
      });
  }
  const hasAny = rows.length > 0;
  return (
    <div className="rounded-md bg-muted/40 p-3 space-y-1 text-xs">
      <div className="flex justify-between">
        <span>Gross amount</span>
        <span className="tabular-nums">{fmtCurrency(preview.gross, c)}</span>
      </div>
      {rows.map((r) => (
        <div key={r.label} className="flex justify-between text-muted-foreground">
          <span>{r.label}</span>
          <span className="tabular-nums">{r.value}</span>
        </div>
      ))}
      <div className="border-t pt-1 mt-1 flex justify-between font-semibold">
        <span>{hasAny ? 'Net payout' : 'You receive'}</span>
        <span className="tabular-nums">{fmtCurrency(preview.net, c)}</span>
      </div>
    </div>
  );
}

function badgeFor(
  s: PayoutRequest['status'],
): 'success' | 'warning' | 'destructive' | 'muted' | 'outline' {
  switch (s) {
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'cancelled':
      return 'muted';
    case 'requested':
    case 'queued':
    case 'processing':
      return 'warning';
    default:
      return 'outline';
  }
}

/**
 * The QR this payout will actually be sent to. Read-only here on purpose — the
 * member manages saved QRs in Settings → Payout QR, and whatever is saved for
 * the selected method is SNAPSHOTTED onto the request at submit time, so the
 * finance team reviews the image as it was when the request was made.
 *
 * When the tenant sets `payout_qr_required` (or the method sets `requiresQr`),
 * a missing QR is a blocker rather than a note — the parent disables submit and
 * the server rejects regardless.
 *
 * Renders NOTHING when no QR is saved and none is required. A tenant that never
 * asked for QRs was otherwise shown a dashed placeholder and a "you haven't
 * saved one yet" nag on every cash-out, for a feature it does not use.
 */
function PayoutQrNotice({
  methodCode,
  methodLabel,
  required,
}: {
  methodCode: string;
  methodLabel: string;
  required: boolean;
}) {
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });
  if (!methodCode || qrsQ.isLoading) return null;
  const qr = (qrsQ.data ?? []).find((q) => q.methodCode === methodCode);
  // Nothing saved and nothing required: there is no QR story to tell here.
  if (!qr && !required) return null;
  const blocking = required && !qr;

  return (
    <div
      className={
        blocking
          ? 'rounded-md border border-warning/30 bg-warning/10 p-3'
          : 'rounded-md border bg-muted/20 p-3'
      }
    >
      <div className="flex items-start gap-3">
        {qr ? (
          <img
            src={qr.qrUrl}
            alt={`${methodLabel} payout QR`}
            className="h-20 w-20 shrink-0 rounded border bg-background object-contain"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded border border-dashed text-muted-foreground">
            <QrCode className="h-6 w-6" />
          </div>
        )}
        <div className="min-w-0 text-xs">
          <p className="font-medium">QR code for this payout{blocking ? ' — required' : ''}</p>
          {/* `!qr` implies `required` here — the un-saved, un-required case
              returned null above, so there is no third branch. */}
          {qr ? (
            <p className="mt-0.5 text-muted-foreground">
              {qr.label || `Your saved ${methodLabel} QR`} will be attached to this request.
            </p>
          ) : (
            <p className="mt-0.5 text-muted-foreground">
              {methodLabel} payouts need a QR code. Save the QR your payment should be sent to
              before requesting a cash-out.
            </p>
          )}
          <Link
            to="/settings?tab=payout-qr"
            className="mt-1 inline-block text-primary underline underline-offset-2"
          >
            {qr ? 'Change in Settings' : 'Add one in Settings'}
          </Link>
        </div>
      </div>
    </div>
  );
}
